import React, { useEffect, useMemo, useState } from 'react';
import {
  CategoryDrawModel,
  CompetitionFormat,
  formatLabel,
  getExpectedGroupSizes,
  isMixedEligible,
  isValidMixedGroupDistribution,
  moveParticipantToGroup,
  RoundRobinStandingModel,
} from '../../utils/categoryDrawApi';
import './CategoryDrawPanel.scss';

interface CategoryDrawPanelProps {
  draw: CategoryDrawModel;
  isFormatOverridden: boolean;
  manualFormat: CompetitionFormat;
  isApproved?: boolean;
  onFormatOverrideChange: (overridden: boolean) => void;
  onManualFormatChange: (format: CompetitionFormat) => void;
  onOpenOlympicPreview?: () => void;
  onOpenBracket?: () => void;
  onPrintProtocol?: () => void;
  printingProtocol?: boolean;
  onApprove?: (draw: CategoryDrawModel) => void;
  approving?: boolean;
  loadingPreview?: boolean;
}

export const CategoryDrawPanel: React.FC<CategoryDrawPanelProps> = ({
  draw,
  isFormatOverridden,
  manualFormat,
  onFormatOverrideChange,
  onManualFormatChange,
  onOpenOlympicPreview,
  onOpenBracket,
  onPrintProtocol,
  printingProtocol = false,
  onApprove,
  approving,
  loadingPreview,
  isApproved = false,
}) => {
  const [mixedDraw, setMixedDraw] = useState(draw);

  useEffect(() => {
    setMixedDraw(draw);
  }, [
    draw.tournamentId,
    draw.participantCount,
    draw.format,
    draw.groups.map(g => `${g.groupIndex}:${g.participants.map(p => p.id).join(',')}`).join('|'),
    draw.participants.map(p => p.id).join(','),
  ]);

  const isRoundRobin = draw.format === CompetitionFormat.RoundRobin;
  const isMixed = draw.format === CompetitionFormat.Mixed;
  const matches = draw.rounds.flatMap(r => r.matches);
  const [expectedGroup1, expectedGroup2] = getExpectedGroupSizes(draw.participantCount);

  const unassignedParticipants = useMemo(() => {
    if (!isMixed) return [];
    const assignedIds = new Set(
      mixedDraw.groups.flatMap(g => g.participants.map(p => Number(p.id)))
    );
    return draw.participants.filter(p => !assignedIds.has(Number(p.id)));
  }, [isMixed, mixedDraw, draw.participants]);

  const canApproveMixed = isMixed
    && mixedDraw.groups.length === 2
    && unassignedParticipants.length === 0
    && isValidMixedGroupDistribution(
      mixedDraw.participantCount,
      mixedDraw.groups.find(g => Number(g.groupIndex) === 1)?.participants.length ?? 0,
      mixedDraw.groups.find(g => Number(g.groupIndex) === 2)?.participants.length ?? 0,
    );

  const handleMove = (participantId: number, groupIndex: 1 | 2) => {
    setMixedDraw(prev => moveParticipantToGroup(prev, Number(participantId), groupIndex));
  };

  const renderStandingsTable = (standings: RoundRobinStandingModel[], participants: typeof draw.participants) => (
    <table className="category-draw__table">
      <thead>
        <tr>
          <th>Место</th>
          <th>Участник</th>
          <th>Организация</th>
          <th>Победы</th>
          <th>Поражения</th>
          <th>Очки</th>
          <th>Иппон</th>
        </tr>
      </thead>
      <tbody>
        {(standings.length > 0 ? standings : participants.map((p, i) => ({
          participantId: p.id,
          personName: p.personName,
          organization: p.organization,
          wins: 0,
          losses: 0,
          points: 0,
          ippons: 0,
          place: i + 1,
        } as RoundRobinStandingModel))).map((s) => (
          <tr key={s.participantId}>
            <td>{s.place ?? '—'}</td>
            <td>{s.personName}</td>
            <td>{s.organization || '—'}</td>
            <td>{s.wins}</td>
            <td>{s.losses}</td>
            <td><strong>{s.points}</strong></td>
            <td>{s.ippons}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const openBracketLabel = isMixed
    ? 'Открыть группы'
    : isRoundRobin
      ? 'Открыть таблицу'
      : 'Открыть сетку';

  const canPrintProtocol = isApproved
    && !!onPrintProtocol
    && (draw.format === CompetitionFormat.Olympic || draw.format === CompetitionFormat.Mixed);

  return (
    <div className={`category-draw ${isApproved ? 'category-draw--approved' : ''}`}>
      {isApproved && (
        <div className="category-draw__approved-banner">
          <strong>Группа утверждена.</strong> Состав и сетка сохранены — можно проводить поединки.
        </div>
      )}

      <div className="category-draw__meta">
        <div className="category-draw__meta-row">
          <span>Участников: <strong>{draw.participantCount}</strong></span>
          <span>
            Формат: <strong>{formatLabel(draw.format)}</strong>
            {!isFormatOverridden && draw.autoDetectedFormat !== draw.format && (
              <> (авто: {formatLabel(draw.autoDetectedFormat)})</>
            )}
          </span>
        </div>

        {!isApproved && (
          <div className="category-draw__format-controls">
            <label className="category-draw__override">
              <input
                type="checkbox"
                checked={isFormatOverridden}
                onChange={(e) => onFormatOverrideChange(e.target.checked)}
              />
              Указать формат вручную
            </label>
            {isFormatOverridden && (
              <select
                value={manualFormat}
                onChange={(e) => onManualFormatChange(e.target.value as CompetitionFormat)}
                className="category-draw__format-select"
              >
                <option value={CompetitionFormat.Olympic}>Олимпийская</option>
                <option value={CompetitionFormat.RoundRobin}>Круговая</option>
                <option value={CompetitionFormat.Mixed} disabled={!isMixedEligible(draw.participantCount)}>
                  Смешанная (6–8)
                </option>
              </select>
            )}
          </div>
        )}
      </div>

      {isApproved && (onOpenBracket || canPrintProtocol) && (
        <div className="category-draw__actions category-draw__actions--top">
          {onOpenBracket && (
            <button
              type="button"
              className="category-draw__open-bracket-btn"
              onClick={onOpenBracket}
            >
              {openBracketLabel} →
            </button>
          )}
          {canPrintProtocol && (
            <button
              type="button"
              className="category-draw__print-protocol-btn"
              onClick={onPrintProtocol}
              disabled={printingProtocol}
            >
              {printingProtocol ? 'PDF…' : 'Печать протокола'}
            </button>
          )}
        </div>
      )}

      {draw.participantCount < 2 ? (
        <p className="category-draw__empty">Недостаточно участников для проведения группы (минимум 2).</p>
      ) : isMixed ? (
        <>
          {!isApproved && (
            <p className="category-draw__hint">
              Распределите участников по группам A и B ({expectedGroup1}+{expectedGroup2}).
              После группового этапа автоматически создаётся плей-офф: 1-е A vs 2-е B, 1-е B vs 2-е A.
            </p>
          )}

          <div className="category-draw__mixed-grid">
            {mixedDraw.groups.map(group => (
              <div key={group.groupIndex} className="category-draw__mixed-column">
                <h4 className="category-draw__section-title">
                  Группа {group.groupIndex === 1 ? 'A' : 'B'} ({group.participants.length}/{group.expectedSize})
                </h4>
                <ul className="category-draw__mixed-list">
                  {group.participants.map(p => (
                    <li key={p.id} className="category-draw__mixed-item">
                      <span>{p.personName}</span>
                      {!isApproved && (
                        <div className="category-draw__mixed-actions">
                          {Number(group.groupIndex) !== 1 && (
                            <button type="button" onClick={() => handleMove(p.id, 1)}>→ A</button>
                          )}
                          {Number(group.groupIndex) !== 2 && (
                            <button type="button" onClick={() => handleMove(p.id, 2)}>→ B</button>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {!isApproved && unassignedParticipants.length > 0 && (
            <div className="category-draw__mixed-unassigned">
              <h4 className="category-draw__section-title">Не распределены</h4>
              <ul className="category-draw__mixed-list">
                {unassignedParticipants.map(p => (
                  <li key={p.id} className="category-draw__mixed-item">
                    <span>{p.personName}</span>
                    <div className="category-draw__mixed-actions">
                      <button type="button" onClick={() => handleMove(p.id, 1)}>→ A</button>
                      <button type="button" onClick={() => handleMove(p.id, 2)}>→ B</button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!isApproved && onApprove && (
            <div className="category-draw__actions">
              <button
                className="category-draw__approve-btn"
                onClick={() => onApprove(mixedDraw)}
                disabled={approving || !canApproveMixed}
              >
                {approving ? 'Утверждение…' : 'Утвердить группу'}
              </button>
            </div>
          )}
        </>
      ) : isRoundRobin ? (
        <>
          <h4 className="category-draw__section-title">Таблица очков</h4>
          <div className="category-draw__table-wrap">
            {renderStandingsTable(draw.standings, draw.participants)}
          </div>

          {!isApproved && (
            <>
              <h4 className="category-draw__section-title">Предстоящие поединки ({matches.length})</h4>
              <ul className="category-draw__matches">
                {matches.map((m, idx) => (
                  <li key={idx} className="category-draw__match">
                    <span>{m.homeParticipantName || 'TBD'}</span>
                    <span className="category-draw__vs">vs</span>
                    <span>{m.awayParticipantName || 'TBD'}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {!isApproved && onApprove && (
            <div className="category-draw__actions">
              <button
                className="category-draw__approve-btn"
                onClick={() => onApprove(draw)}
                disabled={approving}
              >
                {approving ? 'Утверждение…' : 'Утвердить группу'}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="category-draw__olympic">
          <p className="category-draw__hint">
            Для олимпийской системы откройте предпросмотр сетки, при необходимости поменяйте участников местами и утвердите.
          </p>
          {onOpenOlympicPreview && (
            <button
              className="category-draw__preview-btn"
              onClick={onOpenOlympicPreview}
              disabled={loadingPreview}
            >
              {loadingPreview ? 'Загрузка…' : 'Предпросмотр сетки'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryDrawPanel;
